import { type v2 } from '@deque/billing-service-client';
import { getProductSubscription } from '@deque/billing-utils';
import { ProductSlugs } from '~/common/constants';
import { type AxeMCPServerCardProps } from '../components/AxeMCPServerCard';

interface Params {
  hasFreemiumAxeMCPServer: boolean;
  activeEnterprise?: v2.Enterprise | null;
  isAdmin: boolean;
  htmlProduct?: v2.Product;
  billingUser: v2.User | null;
}

/**
 * Resolves the props for the Axe MCP Server card, or `null` when the card must not
 * render. The card is not catalog-driven: it is MCP-branded but activates a free tier
 * of `axe-devtools-html`, so its visibility follows that product's subscription.
 *
 * The card is hidden unless the freemium flag is on, an enterprise is active, and the
 * `axe-devtools-html` product resolves. A subscription in any state other than `none`
 * or `free` means the org's entitlement is not ours to represent (and the activation
 * endpoint would `409`).
 */
const getAxeMCPServerCardProps = ({
  hasFreemiumAxeMCPServer,
  activeEnterprise,
  isAdmin,
  htmlProduct,
  billingUser
}: Params): AxeMCPServerCardProps | null => {
  if (!hasFreemiumAxeMCPServer || !activeEnterprise || !htmlProduct) {
    return null;
  }

  const purchaseState = getProductSubscription(
    activeEnterprise.subscriptions,
    ProductSlugs.axeDevToolsHTML
  )?.purchase_state;

  if (purchaseState && purchaseState !== 'none' && purchaseState !== 'free') {
    return null;
  }

  const hasSeat = !!getProductSubscription(
    billingUser?.enterprises.find(({ id }) => id === activeEnterprise.id)
      ?.subscriptions ?? [],
    ProductSlugs.axeDevToolsHTML
  );

  return {
    htmlProductId: htmlProduct.id,
    enterpriseId: activeEnterprise.id,
    isAdmin,
    status:
      purchaseState !== 'free'
        ? 'activatable'
        : isAdmin || hasSeat
          ? 'active'
          : 'awaitingAccess'
  };
};

export default getAxeMCPServerCardProps;
